import { getJobDates, showLocation, showPay } from '@/components/JobInformation';
import JobStatusTag from '@/components/JobStatusTag/JobStatusTag';
import { IJobDetail } from '@/types/jobs';
import { pushGtmEvent } from '@/utils/gtm';
import {
  getJobDisplayTitle,
  getJobHighlights,
  getPostedAgo,
  isNewJob,
} from '@/utils/jobCardHelper';
import { generateJobDetailsURL } from '@/utils/jobListingHelper';
import { useAnalytics } from '@/utils/useAnalytics';
import cx from 'classnames';
import Link from 'next/link';
import React, { ReactElement, useEffect, useRef, useState } from 'react';

import { Typography } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarTodayOutlined';
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined';
import ScheduleIcon from '@material-ui/icons/ScheduleOutlined';

import styles from './JobCard.module.scss';

/**
 * JobCard displays the job information in a card layout.
 * It accepts 2 optional elements (topButtonElement and bottomButtonElement) we passed these two elements
 * if we want to have some action button displayed to the job card e.g (withdraw button, enquire button, etc.)
 * @param data - Job details data
 * @param topButtonElement - Element that we want to display at the top-right corner of the job card
 * @param bottomButtonElement - Element that we want to display at the bottom-right corner of the job card
 * @param className - Additional class we want to apply in the main card
 */
interface JobCardProps {
  data: IJobDetail;
  topButtonElement?: ReactElement;
  bottomButtonElement?: ReactElement;
  className?: string;
  isJobSummary?: boolean;
  isHighlighted?: boolean;
  // Enables GTM dataLayer tracking (job_view_details, job_save, job_impression) for this card.
  enableGtmTracking?: boolean;
  // 1-based position of this card within its list, used for job_impression tracking.
  position?: number;
}

function readJobTrackingData(cardElement: HTMLElement) {
  const { jobId, jobTitle, jobSpeciality, jobGrade, jobLocation, jobWorkType, pageCanonical } =
    cardElement.dataset;
  return {
    job_id: jobId ?? '',
    job_title: jobTitle ?? '',
    job_speciality: jobSpeciality ?? '',
    job_grade: jobGrade ?? '',
    job_location: jobLocation ?? '',
    job_work_type: jobWorkType ?? '',
    page_canonical: pageCanonical ?? '',
  };
}

const JobCard = ({
  data,
  topButtonElement,
  bottomButtonElement,
  className,
  isJobSummary = false,
  isHighlighted = false,
  enableGtmTracking = false,
  position,
}: JobCardProps) => {
  const track = useAnalytics();
  const cardRef = useRef<HTMLElement>(null);
  const hasTrackedImpressionRef = useRef(false);
  const [pageCanonical, setPageCanonical] = useState('');
  const { jobNumber, workType, city, country, locationDisplay, createdAt } = data;
  const subspcialityOrSpeciality = data.subspeciality ? data.subspeciality : data.speciality;
  const displayTitle = getJobDisplayTitle(data);
  const jobDetailsPath = generateJobDetailsURL(
    data.jobNumber,
    data.grade,
    subspcialityOrSpeciality,
  );

  const dateRange = getJobDates(workType, data.startDate, data.endDate);
  const salary = showPay(
    workType,
    data.payRate,
    data.payInterval,
    data.salaryFrom,
    data.salaryTo,
    data.ofBillings,
    data.guaranteedHourlyRate,
  );
  const highlights = getJobHighlights(data.descriptionPlainText);
  const location = showLocation({ city, state: locationDisplay, country });
  const locationText =
    [city, locationDisplay].filter(Boolean).join(', ') + (country ? ` ${country}` : '');

  useEffect(() => {
    if (!enableGtmTracking) {
      return;
    }
    setPageCanonical(document.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? '');
  }, [enableGtmTracking]);

  useEffect(() => {
    if (
      !enableGtmTracking ||
      !cardRef.current ||
      hasTrackedImpressionRef.current ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedImpressionRef.current) {
          hasTrackedImpressionRef.current = true;
          pushGtmEvent({
            event: 'job_impression',
            element: 'Job Card',
            job_id: data.id,
            position: position ?? '',
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [enableGtmTracking, data.id, position]);

  // Attached imperatively (rather than via a JSX onClick) since this is a delegated analytics
  // listener on a non-interactive element, not an interactive affordance of the card itself —
  // the actual buttons inside remain independently keyboard-accessible.
  useEffect(() => {
    const cardElement = cardRef.current;
    if (!enableGtmTracking || !cardElement) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest('[data-testid="job-button"]')) {
        pushGtmEvent({
          event: 'job_view_details',
          element: 'See Details',
          ...readJobTrackingData(cardElement),
        });
      } else if (target.closest('[data-testid="job-favourite-button"]')) {
        pushGtmEvent({
          event: 'job_save',
          element: 'Bookmark',
          ...readJobTrackingData(cardElement),
        });
      }
    };

    cardElement.addEventListener('click', handleClick);
    return () => cardElement.removeEventListener('click', handleClick);
  }, [enableGtmTracking]);

  return (
    <article
      ref={cardRef}
      className={cx(className, styles.container, isHighlighted && styles.highlighted)}
      data-testid="job-card"
      data-job-id={data.id}
      data-job-title={displayTitle}
      data-job-speciality={subspcialityOrSpeciality}
      data-job-grade={data.grade}
      data-job-location={locationText}
      data-job-work-type={workType}
      data-page-canonical={pageCanonical || undefined}
      data-card-position={position}
    >
      <div className={styles.topRow}>
        <div className={styles.tags} data-testid="job-card-tags">
          {isNewJob(createdAt) && <JobStatusTag label="New" variant="new" />}
          {workType && <JobStatusTag label={workType} />}
        </div>
        <div className={styles.topButton}>{topButtonElement}</div>
      </div>

      <Link
        prefetch={false}
        href={jobDetailsPath}
        as={jobDetailsPath}
        passHref
        className={styles.title}
        onClick={() => {
          track('Job Details', {
            'Details Origin': 'Job Card',
            'Details Action': 'Title Link',
            jobNumber: jobNumber,
          });
        }}
      >
        <Typography
          variant="h5"
          component={isJobSummary ? 'h2' : 'h1'}
          data-testid="job-card-title"
        >
          {displayTitle}
        </Typography>
      </Link>

      <div className={styles.detailsRow}>
        <div className={styles.infoColumn}>
          <div className={styles.infoRow} data-testid="job-card-location">
            <LocationOnIcon className={styles.infoIcon} />
            <span>{location}</span>
          </div>
          <div className={styles.infoRow} data-testid="job-card-date">
            <CalendarTodayIcon className={styles.infoIcon} />
            <span>{dateRange}</span>
          </div>
          <div className={styles.infoRow} data-testid="job-card-posted">
            <ScheduleIcon className={styles.infoIcon} />
            <span>{getPostedAgo(createdAt)}</span>
          </div>
        </div>

        {highlights.length > 0 && (
          <div className={styles.highlights} data-testid="job-card-highlights">
            {highlights.map((highlight, index) => (
              <div className={styles.highlightRow} key={index}>
                <span className={styles.bullet} aria-hidden="true">
                  &bull;
                </span>
                <span>{highlight}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.divider} />

      <div className={styles.bottomRow}>
        <div className={styles.salary} data-testid="job-card-salary">
          <span className={styles.salaryAmount}>{salary}</span>
          <span className={styles.salaryLabel}>Base salary</span>
        </div>
        <div className={styles.bottomButton}>{bottomButtonElement}</div>
      </div>
    </article>
  );
};

export default JobCard;
